import { Workflow } from "@/types/Workflow";
import { apiGet, apiPost, apiPut, apiDelete } from "../_apiShim";
import { withAiContentTool } from "@/lib/ai/aiContentQuery";

const BASE_PATH = "/api/v1/workflows/";

export async function getWorkflows(
  eventId: string,
): Promise<Workflow[]> {
  return await apiGet<Workflow[]>(`${BASE_PATH}${eventId}`, {
    "Cache-Control": "no-store",
  }, true);
}

export async function getWorkflow(
  eventId: string,
  pipeId: string,
): Promise<Workflow> {
  return await apiGet<Workflow>(`${BASE_PATH}${eventId}/${pipeId}`, {
    "Cache-Control": "no-store",
  }, true);
}

/**
 * `aiContentTool` (Art. 50 Abs. 2 KI-VO, plan rev. 4.2 J3): the AI tool behind
 * the draft (`apply_workflow_wizard_state` when Velo wrote a literal guest
 * field value). The body IS the workflow entity, so it travels as
 * `?aiContentTool=`; omitted ⇒ the request is unchanged.
 */
export async function createWorkflow(
  eventId: string,
  pipe: Partial<Workflow>,
  aiContentTool?: string,
): Promise<Workflow> {
  return await apiPost<Workflow>(
    withAiContentTool(`${BASE_PATH}${eventId}`, aiContentTool),
    pipe,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/**
 * `expectedUpdatedAt` (VERTRAG_toggle): the `updatedAt` this caller loaded
 * the workflow with, passed back verbatim. The backend refuses with 409
 * `workflowChanged` when the stored workflow moved on since — so a list
 * switch can never write over a newer change. Omitted when unknown.
 * `aiContentTool`: as on `createWorkflow` (`?aiContentTool=`, J3).
 */
export async function updateWorkflow(
  eventId: string,
  pipeId: string,
  pipe: Partial<Workflow>,
  opts: { expectedUpdatedAt?: string; aiContentTool?: string } = {},
): Promise<void> {
  const query = opts.expectedUpdatedAt
    ? `?expectedUpdatedAt=${encodeURIComponent(opts.expectedUpdatedAt)}`
    : "";
  await apiPut<void>(
    withAiContentTool(`${BASE_PATH}${eventId}/${pipeId}${query}`, opts.aiContentTool),
    pipe,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function deleteWorkflow(
  eventId: string,
  pipeId: string,
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}/${pipeId}`, {
    "Cache-Control": "no-store",
  });
}

export async function bulkDeleteWorkflows(
  eventId: string,
  workflowIds: string[],
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/bulk-delete`,
    { workflowIds },
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function bulkDuplicateWorkflows(
  eventId: string,
  workflowIds: string[],
): Promise<Workflow[]> {
  return await apiPost<Workflow[]>(
    `${BASE_PATH}${eventId}/bulk-duplicate`,
    { workflowIds },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

/** W4-b: the recipients preview request — every field optional. */
export type RecipientsPreviewRequest = {
  workflowId?: string;
  conditions?: Record<string, unknown> | null;
  sendClass?: "MARKETING" | "ORGANISATIONAL";
  page?: number;
  size?: number;
};

export type RecipientsPreviewItem = {
  guestId: string;
  displayName: string | null;
  email: string | null;
};

/** The response shape of POST /workflows/{eventId}/preview-recipients. */
export type RecipientsPreviewResponse = {
  count: number;
  countExact: boolean;
  supersededJobsPresent: boolean;
  page: number;
  size: number;
  items: RecipientsPreviewItem[];
  hiddenFromViewer: boolean;
  skippedOptOut: number;
  skippedUndeliverable: number;
  skippedSuppressed?: number;
  /**
   * W4 N1: ORGANISATIONAL only — guests skipped for an active spam complaint
   * (this company's or global) that is not already counted as suppressed;
   * null for MARKETING.
   */
  skippedComplaint?: number | null;
};

/**
 * W4-b (W9): who a scheduled mailing would reach, as the consumer's fan-out
 * decides it. A read behind POST (the condition tree travels in the body);
 * answers while the ACTIONS plugin is off and on archived / plan-locked events.
 */
export async function previewRecipients(
  eventId: string,
  body: RecipientsPreviewRequest,
): Promise<RecipientsPreviewResponse> {
  return await apiPost<RecipientsPreviewResponse>(
    `${BASE_PATH}${eventId}/preview-recipients`,
    body,
    {
      "Cache-Control": "no-store",
      "Content-Type": "application/json",
    },
    true,
  );
}
